import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import usePackScene from "./usePackScene";
import "./HeroPack.css";

const ChapterCaption = ({ index, label }) => {
  const [shown, setShown] = useState({ index, label });
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (index === shown.index && label === shown.label) {
      return undefined;
    }

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce) {
      setShown({ index, label });
      return undefined;
    }

    setFading(true);
    const timer = window.setTimeout(() => {
      setShown({ index, label });
      setFading(false);
    }, 420);

    return () => window.clearTimeout(timer);
  }, [index, label, shown.index, shown.label]);

  return (
    <p
      aria-live="polite"
      className={`heroPack__chapter${fading ? " heroPack__chapter--out" : ""}`}
    >
      {String(shown.index + 1).padStart(2, "0")} — {shown.label}
    </p>
  );
};

const HeroPack = () => {
  const { canvasRef, sectionRef, chapter, chapterDur, chapters, jumpTo } =
    usePackScene();
  const active = chapters[chapter];

  return (
    <section id="home" ref={sectionRef} className="heroPack">
      <canvas ref={canvasRef} className="heroPack__canvas" aria-hidden="true" />
      <div className="heroPack__veil" aria-hidden="true" />
      <div className="hero-grain pointer-events-none" aria-hidden="true" />

      <div className="heroPack__content">
        <ChapterCaption index={chapter} label={active.label} />

        <h1 className="heroPack__title">
          <span className="block">Stories, told with a</span>
          <span className="hero-headline-glow block italic font-serif leading-[1.25] text-[#b2f141]">
            <span aria-hidden="true">
              wolf<span className="heroPack__apostrophe">'</span>s instinct.
            </span>
            <span className="sr-only">wolf's instinct.</span>
          </span>
        </h1>

        <p className="heroPack__intro text-white/70 text-sm sm:text-base max-w-sm mb-2">
          Strategic. Intuitive. Built to move people — not just impress them.
        </p>
        <p className="heroPack__proof text-white/80 text-base sm:text-lg max-w-sm leading-relaxed mb-8">
          15+ years building for labs, nonprofits, and mission-driven teams
        </p>

        <div className="heroPack__actions flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-6 w-full max-w-sm sm:max-w-none mb-8">
          <Link
            to="/book"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#b2f141] hover:bg-[#9fd82f] text-black px-6 md:px-8 py-3.5 md:py-4 font-semibold text-sm md:text-base transition-colors duration-300 min-h-[48px] sm:min-h-[44px] w-full sm:w-auto"
          >
            Start Your Project
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href="#work"
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/25 sm:border-0 text-white sm:text-white/70 hover:text-white text-sm md:text-base font-medium transition-colors duration-300 min-h-[48px] sm:min-h-[44px] py-3 px-6 w-full sm:w-auto"
          >
            See Our Work
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
      </div>

      <ol className="heroPack__rail" aria-label="Pack traits">
        {chapters.map((c, i) => (
          <li key={c.key} className="heroPack__railItem">
            <button
              type="button"
              onClick={() => jumpTo(i)}
              className={`heroPack__railButton${
                i === chapter ? " heroPack__railButton--active" : ""
              }`}
            >
              <span className="heroPack__railLine">
                {i === chapter && (
                  <span
                    className="heroPack__railBar"
                    style={{ "--dur": `${chapterDur}s` }}
                  />
                )}
              </span>
              <span className="heroPack__railLabel">
                {String(i + 1).padStart(2, "0")} {c.label}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
};

export default HeroPack;
